import IconButton from '@/global/components/Button/IconButton'
import { CardAssociacoes } from '@/global/components/CardAssociacoes'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridRenderCellParams } from '@/global/components/DataGrid/utils/types'
import Grid from '@/global/components/ui/Grid'
import dayjs from 'dayjs'
import { SelectEmpresa } from 'global/components/Selects'
import { ContentGuide } from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import { Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Funcionario } from 'service/api'
import { escalas } from 'service/api/endpoints/escalas'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'
import { AdicionarFuncionarioEscala } from '../modals/AdicionarFuncionario'

function FormularioAssocioarFuncionarioEscala() {
    const { id: escalaId } = useParams()
    const empresaId = useStore(state => state.companyId)
    const [isAdicionarAberto, setIsAdicionarAberto] = useState(false)
    const canEditar = hasPermission(['update', 'Funcionários'])

    const { data, refetch, isLoading } = escalas.useFetchByIdFrom<
        Funcionario[]
    >(escalaId!, 'funcionarios', {
        enabled: !!escalaId && !!empresaId,
        queryParams: {
            empresaId,
        },
    })

    const update = async (
        escalaFuncionarioId: number,
        field: string,
        value: string | null,
    ) => {
        if (!['dtInicial', 'dtFinal'].includes(field)) return
        const template = field === 'dtInicial' ? 'Data inicial' : 'Data final'
        try {
            const parse =
                field === 'dtFinal' && !value
                    ? null
                    : z.string().date().nullish().parse(value)
            await escalas.patch('funcionarios', escalaFuncionarioId, {
                [field]: parse,
            })

            await refetch()

            toast.success(`${template} alterada com sucesso`)
        } catch (error: any) {
            if (error instanceof z.ZodError) {
                toast.error(`Erro de validação, ${template} inválida`)
            } else {
                toast.error(error.message)
            }
        }
    }

    return (
        <Grid container className="items-start">
            <Grid item xs={12}>
                <SelectEmpresa variant="form" />
            </Grid>
            <Grid item xs={12}>
                <CardAssociacoes
                    titulo="Funcionários associados"
                    tituloAdicionar="Adicionar funcionário"
                    onAdicionar={
                        canEditar ? () => setIsAdicionarAberto(true) : undefined
                    }
                >
                    <div className="min-h-72 w-full flex-1">
                        <ContentGuide.DataGrid
                            loading={isLoading}
                            density="compact"
                            className="*:border-none"
                            onCellEditCommit={({ field, id, value }) =>
                                update(Number(id), field, value || null)
                            }
                            columns={[
                                {
                                    field: 'Funcionario',
                                    headerName: 'Nome',
                                    flex: 1,
                                    valueGetter: ({ value }) => value.nome,
                                },
                                {
                                    field: 'dtInicial',
                                    headerName: 'Data Inicial',
                                    width: 160,
                                    disableColumnMenu: true,
                                    editable: canEditar,
                                    type: 'date',
                                    valueGetter: ({ value }) => {
                                        return value
                                            ? dayjs
                                                  .utc(value)
                                                  .format('YYYY-MM-DD')
                                            : null
                                    },
                                    valueFormatter: ({ value }) =>
                                        value
                                            ? dayjs
                                                  .utc(value)
                                                  .format('DD/MM/YYYY')
                                            : null,
                                },
                                {
                                    field: 'dtFinal',
                                    headerName: 'Data Final',
                                    width: 160,
                                    disableColumnMenu: true,
                                    editable: canEditar,
                                    type: 'date',
                                    valueGetter: ({ value }) => {
                                        return value
                                            ? dayjs
                                                  .utc(value)
                                                  .format('YYYY-MM-DD')
                                            : null
                                    },
                                    valueFormatter: ({ value }) =>
                                        value
                                            ? dayjs
                                                  .utc(value)
                                                  .format('DD/MM/YYYY')
                                            : null,
                                },
                                ...(canEditar
                                    ? [
                                          {
                                              ...optionsColumn,
                                              field: 'delete',
                                              renderCell: ({
                                                  id: pivotId,
                                              }: GridRenderCellParams) => (
                                                  <Link
                                                      to={`${pivotId}/excluir`}
                                                  >
                                                      <IconButton title="Desassociar">
                                                          <Trash2Icon className="text-error" />
                                                      </IconButton>
                                                  </Link>
                                              ),
                                          },
                                      ]
                                    : []),
                            ]}
                            rows={data}
                            ToolbarProps={{ hideToolbar: true }}
                            hideFooter
                        />
                    </div>
                </CardAssociacoes>
            </Grid>
            <AdicionarFuncionarioEscala
                escalaId={escalaId!}
                open={isAdicionarAberto}
                onClose={() => setIsAdicionarAberto(false)}
                onAssociado={refetch}
            />
        </Grid>
    )
}

export default FormularioAssocioarFuncionarioEscala
